import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Async } from 'react-select';
import _ from 'lodash';

import { getFlexibleRegex } from '../common/editors/autocomplete-filter-logic';
import useModelsData from './data-caches/useModelsData';
import useDragTransfer from '../common/react-hooks/useDragTransfer';
import TrimSpec from './trims/TrimSpec';
import useContextValidator from './data-caches/useContextValidator';
import { isFacelift, getBaseGenId } from '../../../../model/generations/ModelGenerationsData.mjs';

let SPECS = {
  'fuel': ['diesel', 'nafta', 'electric'],
  'body': ['sedan', 'hatchback', 'pickup', 'coupecabriolet', 'familiar'],
  'engine': [''],
  'country': ['ar', 'mx', 'es', 'cl', 'co', 'us'],
}

let SPECIAL_TOKENS = {
  _source: ['inferred_from_pictures'],
  _inherit: ['no'],
}

const DRAG_MIME = 'application/model-context-spec';

function specValToLabel(val) {
  return val?.$regex != null ? '/' + val.$regex + '/' : val;
}

function yearsToLabel(years) {
  const sorted = [...years].sort((a, b) => a - b);
  const continuous = sorted.length > 1 && sorted.every((y, idx) => idx === 0 || y === sorted[idx-1] + 1);
  return continuous ? sorted.length > 2 ? `${sorted[0]} ... ${sorted[sorted.length-1]}` : `${sorted[0]}-${sorted[sorted.length-1]}` : sorted.join(', ');
}

export default function ModelContextEditor({ value, onChange, contextObject = true, groupsToUse = null, locale = null, ...otherProps }) {
  const handleDrop = useCallback((spec) => {
    if (contextObject) {
      onChange({ ...value, ...spec });
    } else {
      const [field, val] = Object.entries(spec)[0];
      onChange([...(value || []), [field, val]]);
    }
  }, [value, onChange, contextObject]);

  const { draggableProps, dropZoneProps, dropZoneStyle } = useDragTransfer(DRAG_MIME, { onDrop: handleDrop });
  const data = useModelsData();
  const [isValid, explanation] = useContextValidator(value, locale);

  // Current modelId/country of the context (object form, or the [field, val] pairs form).
  const ctxField = (field) => {
    const val = contextObject ? value?.[field] : _.find(value, ([f]) => f === field)?.[1];
    return typeof val === 'string' ? val : null;
  };
  const ctxModelId = ctxField('modelId');
  const ctxCountry = ctxField('country') || 'global';

  const {generations, options} = useMemo(() => {
    const options = [];

    // Generation candidates mirror useContextValidator: with a modelId, take the gens declared for
    // that model+country (gens() falls back to the 'global' row). If some are declared, a genId is
    // valid when it (or its base gen) is declared, so declared gens + their catalog facelifts are
    // offered. If none are declared for the model+country, any existing gen or facelift is valid:
    // the gens declared for this nameplate in ANY country (a country may map model A to a gen of a
    // different base model) and their facelifts come first, then the rest of the catalog.
    const withFacelifts = (declared) => {
      const declaredIds = new Set(declared.map(g => g.genId));
      const facelifts = data.generations()
        .filter(g => isFacelift(g.genId) && !declaredIds.has(g.genId) && declaredIds.has(getBaseGenId(g.genId)))
        .map(g => data.getGenDef(g.genId, { modelId: ctxModelId, country: ctxCountry }) || g);
      return [...declared, ...facelifts];
    };

    let generationItems;
    const declared = ctxModelId ? _.uniqBy(data.gens(ctxModelId, ctxCountry), 'genId') : [];
    if (declared.length) {
      generationItems = withFacelifts(declared);
    } else {
      const anyCountry = ctxModelId ? withFacelifts(_.uniqBy(data.gens(ctxModelId), 'genId')) : [];
      const anyCountryIds = new Set(anyCountry.map(g => g.genId));
      generationItems = [...anyCountry, ...data.generations().filter(g => !anyCountryIds.has(g.genId))];
    }

    const groupItems = {
      models: data.models(),
      makes: data.makes(),
      generations: generationItems,
      families: data.families(),
      submodels: data.submodels(),
      years: _.range(1980, 2028),
      specs: _.flatMap({...SPECS, ...SPECIAL_TOKENS}, (vals,key) => _.map(vals, val => [key,val])),
    };

    const groupToSpec = {
      models: item => ({label: item.replace('-', ' '), value: {modelId: item}}),
      makes: item => ({label: item, value: {make: item}}),
      generations: item => ({
        search: item.name ? `${item.genId} ${item.name} ${(item.codenames || [])?.join(' ')}` : item.genId,
        label: item.genId,
        value: {genId: item.genId},
        fromYear: item.fromYear,
        toYear: item.toYear,
      }),
      families: item => ({label: item.replace('-', ' '), value: {modelId: item}}),
      submodels: item => ({label: item.replace('-', ' '), value: {modelId: item}}),
      years: item => ({label: item.toString(), value: {year: item}}),
      specs: ([key, val]) => ({label: `${key}=${val}`, value: {[key]: val}}),
    }

    for(const group of groupsToUse || Object.keys(groupToSpec)) {
      let getOption = groupToSpec[group];
      options.push(...(groupItems[group] || []).map(item => getOption(item)));
    }

    return { generations: generationItems, options};
  }, [data, ctxModelId, ctxCountry, groupsToUse]);

  const handleSelectChange = (values, actionMeta) => {
    if (contextObject) {
      let mergedContext = {};
      let trimsArr = [];
      _.each(values, ({ value: { trims, ...otherValues } }) => {
        if (trims) {
          trimsArr.push(trims);
        }
        mergedContext = { ...mergedContext, ...otherValues };
      });

      if (trimsArr.length) {
        mergedContext.trims = trimsArr;
      }

      if (mergedContext.modelId && mergedContext.make) {
        if (value.make) {
          delete mergedContext['make'];
        } else {
          delete mergedContext['modelId'];
        }
      }

      if (mergedContext.years && mergedContext.year != null) {
        if (actionMeta?.option?.value?.years) {
          delete mergedContext['year'];
        } else {
          delete mergedContext['years'];
        }
      }

      onChange(mergedContext);
    } else {
      onChange(values.map(v => _.toPairs(v.value)[0]));
    }
  };

  const getOption = (props) => {
    let {innerProps, isSelected, isFocused, data} = props;
    let {innerRef, ... filteredProps} = innerProps;

    let [field, val] = data.value ?  _.toPairs(data.value)[0] : data;
    
    let op = <TrimSpec fullInfo={true} spec={field} value={props.label} className={'mr-1'}/>

    return <div {... filteredProps} className={'p-1 '+((isSelected || isFocused) ? 'bg-light-primary': '')}>
      {op}
    </div>
  };

  const getMultiValueLabel = (props) => {
    let [field, val] = props.data.value ?  _.toPairs(props.data.value)[0] : props.data;
    const display = Array.isArray(val) ? props.data.label : specValToLabel(val);

    return <span {...draggableProps({ [field]: val })}>
      <TrimSpec spec={field} value={display} className={'mr-1'}/>
    </span>
  };

  const values = useMemo(() => {
    let values = [];
    if (contextObject) {
      let { make, modelId, genId, year, trim, trims, years, ...other } = value || {};

      if (make)
        values.push({ label: specValToLabel(make), value: { make } })

      if (modelId)
        values.push({ label: typeof modelId === 'string' ? modelId.replace("-", " ") : specValToLabel(modelId), value: { modelId } })

      if (genId) {
        values.push(_.find(generations, v => v.value?.genId == genId) || { label: genId, value: { genId } })
      }
      if (trim) {
        values.push({ label: trim, value: { trim } })
      }

      if (years && years.length)
        values.push({ label: yearsToLabel(years), value: { years } })
      else if (year)
        values.push({ label: year, value: { year } })

      if (trims) {
        for (const t of trims) {
          values.push({ label: t, value: { trims: t } })
        }
      }

      _.each(other, (val, key) => values.push({ label: key + '=' + specValToLabel(val), value: { [key]: val } }))
    } else {
      _.each(value, ([field, val]) => {
        if (['make', 'modelId', 'year', 'trim'].includes(field)) {
          const label = field === 'modelId' && typeof val === 'string' ? val.replace("-", " ") : specValToLabel(val);
          values.push({ label, value: { [field]: val } })
        } else if (field === 'genId') {
          values.push(_.find(generations, v => v.value?.[field] == val) || { label: val, value: { [field]: val } })
        } else if (field === 'trims') {
          for (const t of val) {
            values.push({ label: t, value: { trims: t } })
          }
        } else {
          values.push({ label: field + '=' + specValToLabel(val), value: { [field]: val } })
        }
      });
    }
    return values;
  }, [value, generations]);


  const loadOptions = async (input) => {
    let regex, flexibleRegex;
    
    try {
      regex = new RegExp((input || "").trim(), 'i');
      flexibleRegex = getFlexibleRegex((input || "").trim());
    } catch(e) {
      regex = new RegExp(_.escapeRegExp(input));
      flexibleRegex = regex;
    }

    let extra = [];
    let suffixExtra = [];
    let matchSpec = input.match(/^([^=\s]+)=([^=]*[^=\s])$/);
    let matchYearRange = input && input.match(/^(\d{4})\s*(?:-|\.\.)\s*(\d{4})$/);
    let matchYearRangePartial = input && !matchYearRange && input.match(/^(\d{4})\s*(?:-|\.\.)\s*(\d{0,3})$/);
    let matchYearList = input && /^\d{4}(?:\s*,\s*\d{4})+\s*,?\s*$/.test(input);
    let matchSingleYear = input && /^\d{4}$/.test(input);
    let matchSingleYearAdd = input && input.match(/^\+(\d{4})$/);

    const buildAddYearOption = (newYear) => {
      const existing = _.uniq([...(value?.years || []), ...(value?.year != null ? [value.year] : [])]);
      
      if (existing.includes(newYear) || !existing.length) 
        return null;
      
      const label = `+${newYear} to ${yearsToLabel(existing)}`;
      return { label, value: { years: [...existing, newYear] } };
    };
    if(input && matchSpec) {
      let [,key, val] = matchSpec;
      if (typeof val === 'string' && val.startsWith('/') && val.endsWith('/')) {
        val = { $regex: val.slice(1, -1), $options: 'i' };
      }
      extra = [{label: input, value: {[key]: val}}]
    } else if (matchYearRange) {
      const from = parseInt(matchYearRange[1]);
      const to = parseInt(matchYearRange[2]);
      if (from <= to) {
        const years = _.range(from, to + 1);
        extra = [{ label: yearsToLabel(years), value: { years } }];
      }
    } else if (matchYearList) {
      const years = _.uniq(input.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n)));
      extra = [{ label: yearsToLabel(years), value: { years } }];
    } else if (matchYearRangePartial) {
      const from = parseInt(matchYearRangePartial[1]);
      const partialTo = matchYearRangePartial[2];
      extra = _.range(from + 1, 2028)
        .filter(y => y.toString().startsWith(partialTo))
        .slice(0, 20)
        .map(to => {
          const years = _.range(from, to + 1);
          return { label: yearsToLabel(years), value: { years } };
        });
    } else if (matchSingleYearAdd) {
      const opt = buildAddYearOption(parseInt(matchSingleYearAdd[1]));
      if (opt) extra = [opt];
    } else if (input && /^\/.*\/$/.test(input.trim())) {
      const trimmed = input.trim();
      extra = [{ label: 'modelId=' + trimmed, value: { modelId: { $regex: trimmed.slice(1, -1), $options: 'i' } } }];
    }

    let extraOptions = [];
    if (matchSingleYear) {
      const existing = _.uniq([...(value?.years || []), ...(value?.year != null ? [value.year] : [])]);
      if (existing.length > 0) {
        const opt = buildAddYearOption(parseInt(input));
        if (opt) 
          extraOptions.push(opt);
      }
    }
    
    let matches = options.concat(extraOptions).map((op) => [op, (op.search || op.label).match(regex) || (op.search || op.label).match(flexibleRegex)]).filter(m => m[1]);  

    let sortedMatches = _.sortBy(matches, ([op, matches]) => {
      const { modelId: opModelId, make: opMake, year: opYear, genId: opGenId, years: opYears } = op.value || {};

      let matchLength = matches[0].length;
      const includesModelBoost = contextObject && value?.modelId && (op.search || op.label).includes(value.modelId) ? -3 : 0;
      const resLength = op.label.length;
      const modelIdBoost = opModelId && op.label.toLowerCase().includes(' ' + input.toLowerCase()) ? -10 : 0;
      const makeBoost = opMake && op.label.toLowerCase().startsWith(input.toLowerCase()) ? -20 : 0;
      const isFullYear = opYear && opYear.toString() === input ? -10 : 0;
      const isFullYears = opYears && input.length === 4 && op.label.includes(input) ? -5 : 0;
      
      // If dealing with a generation and context has year, priorize gens compatible with the year
      const vYear = value?.year || value?.years?.['0'];
      const yearBoost = opGenId && vYear && op.fromYear && (op.fromYear <= vYear && (op.toYear || vYear) >= vYear) ? -1 : 0;

      return isFullYear + isFullYears + includesModelBoost +  makeBoost + modelIdBoost + matchLength + resLength/100 + yearBoost;
    }).map(m => m[0]);

    return [...extra, ...sortedMatches.slice(0,20), ...suffixExtra]
  };

  let invalidWarning;
  if(!isValid) {
    invalidWarning = <div className={'text-danger bg-light-danger p-1 small inline-block rounded-bottom ml-1 px-2'}>{explanation}</div>;
  }

  return (
    <div {...dropZoneProps} style={dropZoneStyle}>
      <Async
        closeOnSelect={true}
        isMulti
        components={{
          Option: getOption,
          MultiValueLabel: getMultiValueLabel,
        }}
        onKeyDown={(e) => {
          // Hack to make shift+Home work and select the text
          if (e.key === 'Home' && e.shiftKey) {
            e.defaultPrevented = true;
          }
        }}
        getOptionValue={(option) => JSON.stringify(option)}
        onChange={handleSelectChange}
        loadOptions={loadOptions}
        placeholder="Write make, model or year..."
        menuShouldScrollIntoView={false}
        maxMenuHeight={170}
        className={isValid ? '' : 'border border-danger bg-light-danger rounded'}
        styles={{ menu: (provided) => ({...provided, zIndex: 1000})}}
        value={values}
        {...otherProps}
      />
      { invalidWarning }
    </div>
  );
}
